import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight, AlertCircle, Plus, Wallet, X, Trash2, Loader2,
  ChevronDown, ChevronUp, Banknote, UserPlus, Search, Check, ExternalLink,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../../utils/errorHelpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtEur = (n) => `${Number(n || 0).toFixed(2)} €`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return '—'; }
};

const ageColor = (days) => {
  if (days == null) return 'text-zinc-500';
  if (days >= 30) return 'text-red-400';
  if (days >= 15) return 'text-orange-400';
  return 'text-zinc-400';
};

const CustomerCreditTab = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [showNewDebtModal, setShowNewDebtModal] = useState(false);

  const fetchList = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/credit/customers`);
      setCustomers(res.data);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Erreur de chargement'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const totalOwed = customers.reduce((s, c) => s + (c.total_balance || 0), 0);

  if (loading) {
    return <p className="text-zinc-500 text-sm py-12 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /> Chargement…</p>;
  }

  return (
    <div className="space-y-4" data-testid="customer-credit-tab">
      {/* Summary KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="border border-white/10 bg-[color:var(--color-surface)] p-4">
          <p className="text-zinc-500 text-xs uppercase tracking-wider">Total dû par les clients</p>
          <p className="text-white text-3xl font-light mt-1"
             style={{ fontFamily: 'Cormorant Garamond, serif' }}
             data-testid="customer-total-balance">
            {fmtEur(totalOwed)}
          </p>
        </div>
        <div className="border border-white/10 bg-[color:var(--color-surface)] p-4">
          <p className="text-zinc-500 text-xs uppercase tracking-wider">Clients débiteurs</p>
          <p className="text-white text-3xl font-light mt-1"
             style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {customers.length}
          </p>
        </div>
        <div className="border border-white/10 bg-[color:var(--color-surface)] p-4">
          <p className="text-zinc-500 text-xs uppercase tracking-wider">Plus ancienne dette</p>
          <p className={`text-3xl font-light mt-1 ${ageColor(Math.max(0, ...customers.map((c) => c.oldest_days || 0)))}`}
             style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {customers.length ? `${Math.max(0, ...customers.map((c) => c.oldest_days || 0))} jours` : '—'}
          </p>
        </div>
        <div className="flex items-center justify-end">
          <button type="button"
            onClick={() => setShowNewDebtModal(true)}
            data-testid="open-new-debt-modal"
            className="flex items-center gap-2 bg-[color:var(--color-primary)] text-black px-4 py-2.5 text-xs uppercase tracking-wider hover:bg-[color:var(--color-primary-hover)]">
            <UserPlus className="w-3.5 h-3.5" />
            Nouvelle dette client
          </button>
        </div>
      </div>

      {customers.length === 0 ? (
        <div className="border border-white/10 bg-[color:var(--color-surface)] p-8 text-center">
          <p className="text-zinc-500 text-sm">Aucun client n'a de dette en cours 🎉</p>
        </div>
      ) : (
        <div className="border border-white/10 bg-[color:var(--color-surface)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-3 text-zinc-500 text-xs uppercase tracking-wider">Client</th>
                <th className="text-right p-3 text-zinc-500 text-xs uppercase tracking-wider">Solde dû</th>
                <th className="text-right p-3 text-zinc-500 text-xs uppercase tracking-wider">Commandes</th>
                <th className="text-right p-3 text-zinc-500 text-xs uppercase tracking-wider">Dettes</th>
                <th className="text-right p-3 text-zinc-500 text-xs uppercase tracking-wider">Ancienneté</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <CustomerCreditRow
                  key={c.user_id}
                  customer={c}
                  expanded={expandedId === c.user_id}
                  onToggle={() => setExpandedId(expandedId === c.user_id ? null : c.user_id)}
                  onChanged={fetchList}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNewDebtModal && (
        <NewClientWithDebtModal
          onClose={() => setShowNewDebtModal(false)}
          onSaved={() => { setShowNewDebtModal(false); fetchList(); }}
        />
      )}
    </div>
  );
};

// =========================================================================

const CustomerCreditRow = ({ customer, expanded, onToggle, onChanged }) => (
  <>
    <tr className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer"
        onClick={onToggle}
        data-testid={`customer-credit-row-${customer.user_id}`}>
      <td className="p-3">
        <p className="text-white text-sm">{customer.name}</p>
        <p className="text-zinc-500 text-xs">{customer.phone}</p>
      </td>
      <td className="p-3 text-right text-[color:var(--color-primary)] font-medium">{fmtEur(customer.total_balance)}</td>
      <td className="p-3 text-right text-zinc-400">{customer.unpaid_orders_count}</td>
      <td className="p-3 text-right text-zinc-400">{customer.legacy_debts_count}</td>
      <td className={`p-3 text-right ${ageColor(customer.oldest_days)}`}>{customer.oldest_days != null ? `${customer.oldest_days} j` : '—'}</td>
      <td className="p-3 text-right text-zinc-500">
        {expanded ? <ChevronUp className="w-4 h-4 inline" /> : <ChevronDown className="w-4 h-4 inline" />}
      </td>
    </tr>
    {expanded && (
      <tr>
        <td colSpan={6} className="bg-black/40 p-4">
          <CustomerDetailPanel userId={customer.user_id} onChanged={onChanged} />
        </td>
      </tr>
    )}
  </>
);

// =========================================================================

const CustomerDetailPanel = ({ userId, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showLegacyForm, setShowLegacyForm] = useState(false);
  const [inspectingOrderId, setInspectingOrderId] = useState(null);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/credit/customers/${userId}`);
      setDetail(res.data);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Erreur de chargement'));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  if (loading) return <p className="text-zinc-500 text-sm">Chargement…</p>;
  if (!detail) return null;

  return (
    <div className="space-y-4" data-testid={`customer-detail-${userId}`}>
      <div className="flex flex-wrap gap-2">
        <button type="button"
          onClick={() => setShowPaymentForm(true)}
          data-testid={`record-payment-btn-${userId}`}
          className="flex items-center gap-2 bg-[color:var(--color-primary)] text-black px-4 py-2 text-xs uppercase tracking-wider hover:bg-[color:var(--color-primary-hover)]">
          <Banknote className="w-3.5 h-3.5" />
          Enregistrer un paiement
        </button>
        <button type="button"
          onClick={() => setShowLegacyForm(true)}
          data-testid={`add-legacy-btn-${userId}`}
          className="flex items-center gap-2 border border-white/20 text-zinc-300 px-4 py-2 text-xs uppercase tracking-wider hover:border-white/40">
          <Plus className="w-3.5 h-3.5" />
          Ajouter une dette héritée
        </button>
      </div>

      {/* Legacy debts */}
      {detail.legacy_debts.length > 0 && (
        <div>
          <p className="text-zinc-500 text-[10px] uppercase tracking-widest mb-2">Dettes héritées</p>
          <ul className="space-y-1">
            {detail.legacy_debts.map((d) => (
              <li key={d.id}
                  className="flex items-center justify-between border border-white/5 bg-black p-3 text-sm"
                  data-testid={`legacy-debt-${d.id}`}>
                <div className="min-w-0">
                  <p className="text-white text-sm truncate">{d.description}</p>
                  <p className="text-zinc-500 text-xs">
                    {d.reference && <span className="mr-2">Réf. {d.reference}</span>}
                    Le {fmtDate(d.debt_date)} · Payé {fmtEur(d.paid_amount)} / {fmtEur(d.amount)}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[color:var(--color-primary)] font-medium">{fmtEur(d.balance)}</span>
                  {d.paid_amount === 0 && (
                    <button type="button"
                      onClick={async () => {
                        if (!window.confirm('Supprimer cette dette ?')) return;
                        try {
                          await axios.delete(`${API_URL}/api/admin/credit/legacy-debt/${d.id}`);
                          fetchDetail(); onChanged?.();
                          toast.success('Dette supprimée');
                        } catch (e) {
                          toast.error(extractErrorMessage(e, 'Échec'));
                        }
                      }}
                      data-testid={`delete-legacy-${d.id}`}
                      className="text-zinc-500 hover:text-red-400">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Unpaid orders */}
      {detail.unpaid_orders.length > 0 && (
        <div>
          <p className="text-zinc-500 text-[10px] uppercase tracking-widest mb-2">Commandes impayées</p>
          <ul className="space-y-1">
            {detail.unpaid_orders.map((o) => (
              <li key={o.id}
                  className="border border-white/5 bg-black p-3 text-sm"
                  data-testid={`unpaid-order-${o.id}`}>
                <button
                  type="button"
                  onClick={() => setInspectingOrderId(o.id)}
                  data-testid={`inspect-order-${o.id}`}
                  className="w-full flex items-center justify-between gap-3 text-left hover:bg-white/[0.02] -m-1 p-1 transition-colors"
                  title="Cliquez pour voir le détail de la commande"
                >
                  <div className="min-w-0">
                    <p className="text-white flex items-center gap-2">
                      Commande #{o.id.slice(-8).toUpperCase()}
                      <span className="text-zinc-600 text-xs">🔍</span>
                    </p>
                    <p className="text-zinc-500 text-xs">
                      {fmtDate(o.created_at)} · Payé {fmtEur(o.paid_amount)} / {fmtEur(o.total)} · {o.items?.length || 0} articles
                    </p>
                  </div>
                  <span className="text-[color:var(--color-primary)] font-medium flex-shrink-0">{fmtEur(o.balance)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {detail.unpaid_orders.length === 0 && detail.legacy_debts.length === 0 && (
        <p className="text-zinc-500 text-sm italic text-center py-4">Aucune dette en cours ✓</p>
      )}

      {/* Payment history (recent) */}
      {detail.payments.length > 0 && (
        <div>
          <p className="text-zinc-500 text-[10px] uppercase tracking-widest mb-2">
            Historique des paiements ({detail.payments.length})
          </p>
          <ul className="space-y-1 max-h-60 overflow-y-auto">
            {detail.payments.map((p) => (
              <li key={p.id}
                  className="flex items-center justify-between border border-white/5 bg-black p-2 text-xs"
                  data-testid={`payment-row-${p.id}`}>
                <div>
                  <p className="text-zinc-300">{fmtDate(p.paid_at)} {p.mode && `· ${p.mode}`}</p>
                  {p.notes && <p className="text-zinc-600 italic mt-0.5">{p.notes}</p>}
                  <p className="text-zinc-600 mt-0.5">
                    {p.allocations.length === 0
                      ? 'Aucune allocation'
                      : `Réparti sur ${p.allocations.length} ligne(s)`}
                  </p>
                </div>
                <span className="text-emerald-400 font-medium">{fmtEur(p.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showPaymentForm && (
        <PaymentModal
          userId={userId}
          detail={detail}
          onClose={() => setShowPaymentForm(false)}
          onSaved={() => { setShowPaymentForm(false); fetchDetail(); onChanged?.(); }}
        />
      )}

      {showLegacyForm && (
        <LegacyDebtModal
          userId={userId}
          onClose={() => setShowLegacyForm(false)}
          onSaved={() => { setShowLegacyForm(false); fetchDetail(); onChanged?.(); }}
        />
      )}

      {inspectingOrderId && (
        <OrderInspectModal
          orderId={inspectingOrderId}
          onClose={() => setInspectingOrderId(null)}
        />
      )}
    </div>
  );
};

// =========================================================================

const PaymentModal = ({ userId, detail, onClose, onSaved }) => {
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState('cash');
  const [notes, setNotes] = useState('');
  const [manual, setManual] = useState(false);
  const [allocations, setAllocations] = useState({}); // { "type-id": amount }
  const [saving, setSaving] = useState(false);

  const numAmount = parseFloat(amount) || 0;
  const items = [
    ...detail.legacy_debts.map((d) => ({ key: `legacy-${d.id}`, type: 'legacy', id: d.id, label: `Dette héritée — ${d.description}`, balance: d.balance, date: d.debt_date })),
    ...detail.unpaid_orders.map((o) => ({ key: `order-${o.id}`, type: 'order', id: o.id, label: `Commande #${o.id.slice(-8).toUpperCase()}`, balance: o.balance, date: o.created_at })),
  ].sort((a, b) => (a.date || '').localeCompare(b.date || ''));

  // Live FIFO preview when not manual
  const fifoPreview = (() => {
    let rem = numAmount;
    return items.map((it) => {
      const applied = Math.min(rem, it.balance);
      rem = Math.round((rem - applied) * 100) / 100;
      return { ...it, applied: Math.round(applied * 100) / 100 };
    });
  })();

  const manualTotal = Object.values(allocations).reduce((s, v) => s + (parseFloat(v) || 0), 0);

  const submit = async () => {
    if (numAmount <= 0) {
      toast.error('Montant invalide');
      return;
    }
    setSaving(true);
    try {
      const body = { amount: numAmount, mode, notes };
      if (manual) {
        body.allocations = items
          .map((it) => ({
            type: it.type,
            id: it.id,
            amount: parseFloat(allocations[it.key]) || 0,
          }))
          .filter((a) => a.amount > 0);
      }
      const res = await axios.post(
        `${API_URL}/api/admin/credit/customers/${userId}/payments`, body
      );
      const r = res.data;
      toast.success(
        r.unallocated > 0
          ? `Paiement enregistré (${r.applied}€ alloué, ${r.unallocated}€ excédentaire)`
          : `Paiement enregistré (${r.applied}€)`
      );
      onSaved();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Échec de l\'enregistrement'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
         data-testid="payment-modal">
      <div className="bg-[color:var(--color-surface)] border border-[color:rgb(var(--primary-rgb)/0.30)] max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl text-white font-light"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Enregistrer un paiement
          </h2>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1">Montant *</label>
            <input
              type="number" step="0.01" min="0" value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="payment-amount-input"
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
          </div>
          <div>
            <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1">Mode</label>
            <select value={mode}
              onChange={(e) => setMode(e.target.value)}
              data-testid="payment-mode-select"
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none">
              <option value="cash">Cash</option>
              <option value="transfer">Virement</option>
              <option value="card">Carte</option>
              <option value="other">Autre</option>
            </select>
          </div>
          <div>
            <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1">Notes</label>
            <input type="text" value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="payment-notes-input"
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
          </div>
        </div>

        <div className="flex items-center gap-2 mb-3">
          <label className="text-zinc-400 text-xs flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={manual}
              onChange={(e) => setManual(e.target.checked)}
              data-testid="payment-manual-toggle"
              className="accent-[color:var(--color-primary)]" />
            Allocation manuelle (sinon FIFO automatique)
          </label>
        </div>

        <p className="text-zinc-500 text-[10px] uppercase tracking-widest mb-2">
          {manual ? 'Allocation manuelle' : 'Aperçu FIFO'}
        </p>
        <ul className="space-y-1 mb-4 max-h-72 overflow-y-auto">
          {items.length === 0 && <li className="text-zinc-600 text-sm italic">Aucune dette à régler</li>}
          {items.map((it) => {
            const fifoApplied = fifoPreview.find((x) => x.key === it.key)?.applied || 0;
            return (
              <li key={it.key}
                  className="flex items-center gap-2 border border-white/5 bg-black p-2 text-xs"
                  data-testid={`alloc-row-${it.id}`}>
                <span className={`px-1.5 py-0.5 uppercase text-[9px] border ${
                  it.type === 'legacy' ? 'border-orange-500/40 text-orange-300' : 'border-blue-500/40 text-blue-300'
                }`}>
                  {it.type === 'legacy' ? 'Héritée' : 'Commande'}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-white truncate">{it.label}</p>
                  <p className="text-zinc-500 text-[10px]">Solde {fmtEur(it.balance)} · {fmtDate(it.date)}</p>
                </div>
                {manual ? (
                  <input
                    type="number" step="0.01" min="0" max={it.balance}
                    value={allocations[it.key] || ''}
                    onChange={(e) => setAllocations({ ...allocations, [it.key]: e.target.value })}
                    data-testid={`alloc-input-${it.id}`}
                    className="w-24 bg-transparent border border-white/10 text-white text-right px-2 py-1 outline-none focus:border-[color:var(--color-primary)]" />
                ) : (
                  <span className={`w-24 text-right ${fifoApplied > 0 ? 'text-emerald-400' : 'text-zinc-700'}`}>
                    {fifoApplied > 0 ? fmtEur(fifoApplied) : '—'}
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        <div className="flex items-center justify-between mb-4 text-xs">
          <p className="text-zinc-500">
            {manual
              ? `Alloué : ${fmtEur(manualTotal)} / ${fmtEur(numAmount)}`
              : `Sera alloué : ${fmtEur(fifoPreview.reduce((s, x) => s + x.applied, 0))} / ${fmtEur(numAmount)}`}
          </p>
          {numAmount > 0 && numAmount > (manual ? manualTotal : fifoPreview.reduce((s, x) => s + x.applied, 0)) && (
            <p className="text-orange-400 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Excédent : {fmtEur(Math.round((numAmount - (manual ? manualTotal : fifoPreview.reduce((s, x) => s + x.applied, 0))) * 100) / 100)}
            </p>
          )}
        </div>

        <div className="flex gap-2">
          <button type="button" onClick={submit}
            disabled={saving || numAmount <= 0 || items.length === 0}
            data-testid="payment-submit"
            className="flex-1 bg-[color:var(--color-primary)] text-black py-3 text-xs uppercase tracking-wider hover:bg-[color:var(--color-primary-hover)] disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : <Wallet className="w-3.5 h-3.5 inline mr-2" />}
            Enregistrer le paiement
          </button>
          <button type="button" onClick={onClose}
            className="border border-white/20 text-zinc-400 px-6 py-3 text-xs uppercase tracking-wider hover:border-white/40">
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};

// =========================================================================

const LegacyDebtModal = ({ userId, onClose, onSaved }) => {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [reference, setReference] = useState('');
  const [debtDate, setDebtDate] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Montant invalide');
      return;
    }
    if (!description.trim()) {
      toast.error('Description requise');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/credit/customers/${userId}/legacy-debt`, {
        amount: parseFloat(amount),
        description: description.trim(),
        reference: reference.trim(),
        debt_date: debtDate ? new Date(debtDate).toISOString() : undefined,
      });
      toast.success('Dette héritée ajoutée');
      onSaved();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Échec'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
         data-testid="legacy-debt-modal">
      <div className="bg-[color:var(--color-surface)] border border-[color:rgb(var(--primary-rgb)/0.30)] max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl text-white font-light"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Dette héritée
          </h2>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-zinc-500 text-xs mb-4">
          Ajoutez une dette antérieure au système (commande WhatsApp, ardoise, etc.).
        </p>

        <div className="space-y-3">
          <div>
            <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1">Montant € *</label>
            <input type="number" step="0.01" min="0" value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="legacy-amount"
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
          </div>
          <div>
            <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1">Description *</label>
            <input type="text" value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Commande WhatsApp #2024-12"
              data-testid="legacy-description"
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
          </div>
          <div>
            <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1">Référence</label>
            <input type="text" value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="(facultatif)"
              data-testid="legacy-reference"
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
          </div>
          <div>
            <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1">Date de la dette</label>
            <input type="date" value={debtDate}
              onChange={(e) => setDebtDate(e.target.value)}
              data-testid="legacy-date"
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <button type="button" onClick={submit}
            disabled={saving}
            data-testid="legacy-submit"
            className="flex-1 bg-[color:var(--color-primary)] text-black py-2.5 text-xs uppercase tracking-wider hover:bg-[color:var(--color-primary-hover)] disabled:opacity-40">
            {saving ? '…' : 'Enregistrer'}
          </button>
          <button type="button" onClick={onClose}
            className="border border-white/20 text-zinc-400 px-5 py-2.5 text-xs uppercase tracking-wider hover:border-white/40">
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerCreditTab;

// =========================================================================
// New-client-with-debt modal (used by header button "Nouvelle dette client")
// =========================================================================

const NewClientWithDebtModal = ({ onClose, onSaved }) => {
  // Mode selection
  const [mode, setMode] = useState('existing'); // 'existing' | 'new'

  // Existing customer search
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  // New customer fields
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  // Debt fields
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [reference, setReference] = useState('');
  const [debtDate, setDebtDate] = useState('');

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/users`);
        if (!cancelled) {
          setAllUsers((res.data || []).filter((u) => u.role === 'customer'));
        }
      } catch (e) {
        if (!cancelled) toast.error(extractErrorMessage(e, 'Erreur de chargement clients'));
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredUsers = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return allUsers.slice(0, 8);
    return allUsers.filter((u) =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.phone || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    ).slice(0, 12);
  })();

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error('Montant invalide');
      return;
    }
    if (!description.trim()) {
      toast.error('Description requise');
      return;
    }
    const payload = {
      amount: amt,
      description: description.trim(),
      reference: reference.trim(),
      debt_date: debtDate ? new Date(debtDate).toISOString() : undefined,
    };
    if (mode === 'existing') {
      if (!selectedUser) {
        toast.error('Sélectionnez un client');
        return;
      }
      payload.user_id = selectedUser.id;
    } else {
      if (!name.trim()) {
        toast.error('Nom du client requis');
        return;
      }
      payload.name = name.trim();
      payload.phone = phone.trim();
      payload.email = email.trim();
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/credit/customers/new-with-debt`, payload);
      const userName = res.data?.user?.name || 'Client';
      toast.success(`Dette enregistrée pour ${userName}`);
      onSaved();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Échec'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
         data-testid="new-debt-modal">
      <div className="bg-[color:var(--color-surface)] border border-[color:rgb(var(--primary-rgb)/0.30)] max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl text-white font-light"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Créer une dette client
          </h2>
          <button type="button" onClick={onClose}
            data-testid="new-debt-close"
            className="text-zinc-500 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Mode toggle */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <button type="button"
            onClick={() => setMode('existing')}
            data-testid="mode-existing"
            className={`px-3 py-2 text-xs uppercase tracking-wider border ${mode === 'existing' ? 'bg-[color:var(--color-primary)] text-black border-[color:var(--color-primary)]' : 'border-white/10 text-zinc-400 hover:border-white/30'}`}>
            <Search className="w-3.5 h-3.5 inline mr-1.5" />
            Client existant
          </button>
          <button type="button"
            onClick={() => setMode('new')}
            data-testid="mode-new"
            className={`px-3 py-2 text-xs uppercase tracking-wider border ${mode === 'new' ? 'bg-[color:var(--color-primary)] text-black border-[color:var(--color-primary)]' : 'border-white/10 text-zinc-400 hover:border-white/30'}`}>
            <UserPlus className="w-3.5 h-3.5 inline mr-1.5" />
            Nouveau client
          </button>
        </div>

        {/* Existing — search & select */}
        {mode === 'existing' && (
          <div className="mb-4 space-y-2">
            <label className="text-zinc-500 text-[10px] uppercase tracking-wider block">Rechercher</label>
            <input type="text" value={search}
              onChange={(e) => { setSearch(e.target.value); setSelectedUser(null); }}
              placeholder="Nom, téléphone ou email"
              data-testid="new-debt-search"
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
            {usersLoading ? (
              <p className="text-zinc-500 text-xs"><Loader2 className="w-3 h-3 inline animate-spin" /> Chargement…</p>
            ) : (
              <ul className="border border-white/5 max-h-48 overflow-y-auto">
                {filteredUsers.length === 0 ? (
                  <li className="p-3 text-zinc-600 text-xs">Aucun client trouvé</li>
                ) : filteredUsers.map((u) => (
                  <li key={u.id}
                      onClick={() => setSelectedUser(u)}
                      data-testid={`new-debt-user-${u.id}`}
                      className={`p-2 cursor-pointer text-sm flex items-center justify-between hover:bg-white/5 ${selectedUser?.id === u.id ? 'bg-[color:rgb(var(--primary-rgb)/0.10)]' : ''}`}>
                    <div className="min-w-0">
                      <p className="text-white truncate">{u.name || '—'}</p>
                      <p className="text-zinc-500 text-xs">{u.phone || u.email || ''}</p>
                    </div>
                    {selectedUser?.id === u.id && <Check className="w-4 h-4 text-[color:var(--color-primary)]" />}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* New — name + phone + email */}
        {mode === 'new' && (
          <div className="mb-4 space-y-3">
            <div>
              <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1">Nom du client *</label>
              <input type="text" value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="new-debt-name"
                className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1">Téléphone</label>
                <input type="tel" value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+32…"
                  data-testid="new-debt-phone"
                  className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
              </div>
              <div>
                <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1">Email</label>
                <input type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="new-debt-email"
                  className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
              </div>
            </div>
            <p className="text-zinc-600 text-[11px]">
              Aucun mot de passe requis. Le client pourra être activé plus tard via Admin → Utilisateurs.
            </p>
          </div>
        )}

        {/* Debt block */}
        <div className="border-t border-white/5 pt-4 space-y-3">
          <p className="text-zinc-500 text-[10px] uppercase tracking-widest">Détails de la dette</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1">Montant € *</label>
              <input type="number" step="0.01" min="0" value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="new-debt-amount"
                className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
            </div>
            <div>
              <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1">Date</label>
              <input type="date" value={debtDate}
                onChange={(e) => setDebtDate(e.target.value)}
                data-testid="new-debt-date"
                className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
            </div>
          </div>
          <div>
            <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1">Description *</label>
            <input type="text" value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex: Ardoise novembre, Commande WhatsApp…"
              data-testid="new-debt-description"
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
          </div>
          <div>
            <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1">Référence</label>
            <input type="text" value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="(facultatif)"
              data-testid="new-debt-reference"
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <button type="button" onClick={submit}
            disabled={saving}
            data-testid="new-debt-submit"
            className="flex-1 bg-[color:var(--color-primary)] text-black py-2.5 text-xs uppercase tracking-wider hover:bg-[color:var(--color-primary-hover)] disabled:opacity-40">
            {saving ? <Loader2 className="w-3.5 h-3.5 inline animate-spin" /> : 'Enregistrer la dette'}
          </button>
          <button type="button" onClick={onClose}
            className="border border-white/20 text-zinc-400 px-5 py-2.5 text-xs uppercase tracking-wider hover:border-white/40">
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};


// =========================================================================
// Read-only order inspector — opens from the unpaid-orders list so the admin
// can verify what was in the order without leaving the accounting view.

const OrderInspectModal = ({ orderId, onClose }) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/orders/${orderId}`);
        if (!cancelled) setOrder(res.data);
      } catch (e) {
        if (!cancelled) toast.error(extractErrorMessage(e, 'Erreur de chargement'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orderId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
         data-testid="order-inspect-modal">
      <div className="bg-[color:var(--color-surface)] border border-[color:rgb(var(--primary-rgb)/0.30)] max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl text-white font-light"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {loading ? 'Chargement…' : `Commande #${(order?.id || orderId).slice(-8).toUpperCase()}`}
          </h2>
          <div className="flex items-center gap-2">
            {order && (
              <Link to={`/admin/commandes?id=${order.id}`}
                onClick={onClose}
                data-testid="open-order-page"
                className="flex items-center gap-1 border border-[color:rgb(var(--primary-rgb)/0.40)] text-[color:var(--color-primary)] hover:bg-[color:var(--color-primary)] hover:text-black px-3 py-1.5 text-xs uppercase tracking-wider transition-colors">
                <ExternalLink className="w-3 h-3" />
                Ouvrir
              </Link>
            )}
            <button type="button" onClick={onClose}
              data-testid="order-inspect-close"
              className="text-zinc-500 hover:text-white p-1">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {loading ? (
          <p className="text-zinc-500 text-sm text-center py-8">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Chargement…
          </p>
        ) : !order ? (
          <p className="text-zinc-500 text-sm text-center py-8">Commande introuvable</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-zinc-500 text-[10px] uppercase tracking-widest">Date</p>
                <p className="text-white">{fmtDate(order.created_at)}</p>
              </div>
              <div>
                <p className="text-zinc-500 text-[10px] uppercase tracking-widest">Statut</p>
                <p className="text-white">{order.status}</p>
              </div>
              <div>
                <p className="text-zinc-500 text-[10px] uppercase tracking-widest">Client</p>
                <p className="text-white">{order.customer_name || '—'}</p>
                <p className="text-zinc-500 text-xs">{order.customer_phone || ''}</p>
              </div>
              <div>
                <p className="text-zinc-500 text-[10px] uppercase tracking-widest">Adresse</p>
                <p className="text-white text-xs break-words">{order.customer_address || '—'}</p>
              </div>
            </div>

            <div>
              <p className="text-zinc-500 text-[10px] uppercase tracking-widest mb-2">
                Articles ({order.items?.length || 0})
              </p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-zinc-500 text-[10px] uppercase tracking-wider border-b border-white/10">
                    <th className="text-left py-1">Produit</th>
                    <th className="text-right py-1">Qté</th>
                    <th className="text-right py-1">€/u</th>
                    <th className="text-right py-1">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(order.items || []).map((it, i) => (
                    <tr key={`${it.product_id}-${i}`} className="border-b border-white/5">
                      <td className="py-2">
                        <p className="text-white">{it.product_name || 'Produit'}</p>
                        {it.product_brand && (
                          <p className="text-zinc-500 text-xs">{it.product_brand}</p>
                        )}
                      </td>
                      <td className="py-2 text-right text-zinc-300">{it.quantity}</td>
                      <td className="py-2 text-right text-zinc-400">{fmtEur(it.unit_price)}</td>
                      <td className="py-2 text-right text-[color:var(--color-primary)]">{fmtEur(it.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="border-t border-white/10 pt-3 flex flex-col gap-1 items-end text-sm">
              {order.subtotal != null && order.subtotal !== order.total && (
                <p className="text-zinc-500">Sous-total : <span className="text-white ml-2">{fmtEur(order.subtotal)}</span></p>
              )}
              {order.extra_amount > 0 && (
                <p className="text-zinc-500">
                  {order.extra_label || 'Frais divers'} :
                  <span className="text-white ml-2">+ {fmtEur(order.extra_amount)}</span>
                </p>
              )}
              <p className="text-base">Total : <span className="text-[color:var(--color-primary)] font-medium ml-2">{fmtEur(order.total)}</span></p>
            </div>

            {order.notes && (
              <div className="border-t border-white/10 pt-3">
                <p className="text-zinc-500 text-[10px] uppercase tracking-widest mb-1">Notes</p>
                <p className="text-zinc-300 text-sm whitespace-pre-line">{order.notes}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
